import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 25,
    fontSize: 9,
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#cfcfcf",
    paddingBottom: 10,
  },

  logoContainer: {
    width: 80,
    justifyContent: "center",
    alignItems: "center",
  },

  logo: {
    width: 65,
    height: 65,
    objectFit: "contain",
  },

  titleContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingRight: 80,
  },

  schoolName: {
    fontSize: 17,
    fontWeight: "bold",
  },

  address: {
    fontSize: 8,
    marginTop: 3,
  },

  contact: {
    fontSize: 8,
    marginTop: 3,
  },

  title: {
    marginTop: 7,
    fontSize: 13,
    fontWeight: "bold",
  },

  filter: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 10,
  },

  filterText: {
    fontSize: 9,
    marginHorizontal: 5,
  },

  table: {
    display: "table",
    width: "100%",
    borderWidth: 0.5,
    borderColor: "#d3d3d3",
  },

  row: {
    flexDirection: "row",
    alignItems: "stretch",
  },

  head: {
    backgroundColor: "#eeeeee",
    fontWeight: "bold",
  },

  cell: {
    padding: 5,
    fontSize: 8,
    textAlign: "center",
    borderWidth: 0.5,
    borderColor: "#d3d3d3",
  },

  totalContainer: {
    marginTop: 10,
    alignItems: "flex-end",
  },

  total: {
    fontSize: 11,
    fontWeight: "bold",
  },
});

const formatDate = (date) => {
  if (!date) return "-";

  const parsedDate = new Date(date);

  if (isNaN(parsedDate.getTime())) {
    return "-";
  }

  return parsedDate.toLocaleDateString("en-GB");
};

const ExpenseReport = ({
  expenses = [],
  institute,
  fromDate,
  toDate,
}) => {

  const grandTotal = expenses.reduce(
    (total, item) =>
      total + (Number(item.amount) || 0),
    0
  );

  return (
    <Document>

      <Page
        size="A4"
        orientation="landscape"
        style={styles.page}
      >

        {/* ================= HEADER ================= */}

        <View style={styles.header}>

          <View style={styles.logoContainer}>

            {institute?.instituteLogo && (
              <Image
                src={institute.instituteLogo}
                style={styles.logo}
              />
            )}

          </View>


          <View style={styles.titleContainer}>

            <Text style={styles.schoolName}>
              {institute?.instituteName || ""}
            </Text>

            <Text style={styles.address}>
              {institute?.instituteAddress || ""}
            </Text>


            <Text style={styles.title}>
              Expense Report
            </Text>

          </View>

        </View>


        {/* ================= DATE FILTER ================= */}

        {(fromDate || toDate) && (

          <View style={styles.filter}>

            {fromDate && (
              <Text style={styles.filterText}>
                From Date: {formatDate(fromDate)}
              </Text>
            )}

            {toDate && (
              <Text style={styles.filterText}>
                To Date: {formatDate(toDate)}
              </Text>
            )}

          </View>

        )}


        {/* ================= TABLE ================= */}

        <View style={styles.table}>

          {/* HEADER */}

          <View style={[styles.row, styles.head]}>

            <Text
              style={[
                styles.cell,
                { width: "5%" }
              ]}
            >
              #
            </Text>

            <Text
              style={[
                styles.cell,
                { width: "18%" }
              ]}
            >
              Name
            </Text>

            <Text
              style={[
                styles.cell,
                { width: "17%" }
              ]}
            >
              Towards
            </Text>

            <Text
              style={[
                styles.cell,
                { width: "10%" }
              ]}
            >
              Amount
            </Text>

            <Text
              style={[
                styles.cell,
                { width: "13%" }
              ]}
            >
              Payment Mode
            </Text>

            <Text
              style={[
                styles.cell,
                { width: "10%" }
              ]}
            >
              Date
            </Text>

            <Text
              style={[
                styles.cell,
                { width: "8%" }
              ]}
            >
              Bill
            </Text>

            <Text
              style={[
                styles.cell,
                { width: "9%" }
              ]}
            >
              Receipt
            </Text>

            <Text
              style={[
                styles.cell,
                { width: "10%" }
              ]}
            >
              Receipt No
            </Text>

          </View>


          {/* DATA */}

          {expenses.map((item, index) => (

            <View
              key={item._id || index}
              style={styles.row}
              wrap={false}
            >

              <Text
                style={[
                  styles.cell,
                  { width: "5%" }
                ]}
              >
                {index + 1}
              </Text>


              <Text
                style={[
                  styles.cell,
                  { width: "18%" }
                ]}
              >
                {item.name || "-"}
              </Text>


              <Text
                style={[
                  styles.cell,
                  { width: "17%" }
                ]}
              >
                {item.towards || "-"}
              </Text>


              <Text
                style={[
                  styles.cell,
                  { width: "10%" }
                ]}
              >

                {Number(item.amount || 0)
                  .toLocaleString("en-IN")}
              </Text>


              <Text
                style={[
                  styles.cell,
                  { width: "13%" }
                ]}
              >
                {item.paymentMode || "-"}
              </Text>


              <Text
                style={[
                  styles.cell,
                  { width: "10%" }
                ]}
              >
                {formatDate(item.date)}
              </Text>


              <Text
                style={[
                  styles.cell,
                  { width: "8%" }
                ]}
              >
                {item.bill ? "Available" : "-"}
              </Text>


              <Text
                style={[
                  styles.cell,
                  { width: "9%" }
                ]}
              >
                {item.receipt ? "Available" : "-"}
              </Text>


              <Text
                style={[
                  styles.cell,
                  { width: "10%" }
                ]}
              >
                {item.receiptNo || "-"}
              </Text>

            </View>

          ))}

        </View>


        {/* ================= TOTAL ================= */}

        <View style={styles.totalContainer}>

          <Text style={styles.total}>
            Grand Total:
            {grandTotal.toLocaleString("en-IN")}
          </Text>

        </View>

      </Page>

    </Document>
  );
};

export default ExpenseReport;